import { getMetricsData } from 'api/getMetricsData';
import { getAllPerformanceMetrics } from 'api/getAllPerformanceMetrics';
import { PRIOR_COMPARISON_DISABLED_DATE } from 'features/globalFilters/globalFiltersDefaults';
import * as Types from './metricsTypes';

const DATE_BASIS_MAP = { paid: 'Month Paid', incurred: 'Month Incurred' };
const METRICS_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
const inFlightMetricsRequests = new Map();

const toIsoDate = (dateStr) => {
  if (!dateStr) return dateStr;
  if (/^\d{4}-\d{2}/.test(dateStr)) return dateStr.substring(0, 7) + '-01';
  const [mm, yyyy] = dateStr.split('/');
  return `${yyyy}-${mm.padStart(2, '0')}-01`;
};

/**
 * Build Tableau filter params from applied global filters
 */
export function buildMetricsFilters(applied) {
  if (!applied) return {};
  const filters = {};

  if (applied.dateBasis) filters.date_range = DATE_BASIS_MAP[applied.dateBasis] || applied.dateBasis;
  if (applied.currentPeriodFrom) filters.current_start = toIsoDate(applied.currentPeriodFrom);
  if (applied.currentPeriodTo) filters.current_end = toIsoDate(applied.currentPeriodTo);
  const priorPeriodFrom = applied.enablePriorComparison === false
    ? PRIOR_COMPARISON_DISABLED_DATE
    : applied.priorPeriodFrom;
  const priorPeriodTo = applied.enablePriorComparison === false
    ? PRIOR_COMPARISON_DISABLED_DATE
    : applied.priorPeriodTo;
  if (priorPeriodFrom) filters.prior_start = priorPeriodFrom === PRIOR_COMPARISON_DISABLED_DATE ? priorPeriodFrom : toIsoDate(priorPeriodFrom);
  if (priorPeriodTo) filters.prior_end = priorPeriodTo === PRIOR_COMPARISON_DISABLED_DATE ? priorPeriodTo : toIsoDate(priorPeriodTo);

  if (applied.divisionLabels?.length > 0) {
    filters.division = applied.divisionLabels.join(',');
  }
  if (applied.planLabels?.length > 0) {
    filters.plan = applied.planLabels.join(',');
  }
  if (applied.personIdValue) {
    const ids = applied.personIdValue.split(/[;\n,]+/).map(s => s.trim()).filter(Boolean);
    if (ids.length > 0) {
      filters.person_id = ids.join(',');
    }
  }

  return filters;
}

/**
 * Generate a short deterministic hash from filters for caching.
 * Produces a 32-bit numeric hash string so cache keys stay small
 * even with large person_id selections.
 */
export function getFilterHash(filters) {
  const keys = Object.keys(filters).sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
  if (keys.length === 0) return '';
  const str = keys.map(k => `${k}=${filters[k]}`).join('&');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
}

/**
 * Generate cache key for metrics data
 */
const getCacheKey = (tenantCd, sheetName, workBookName, filterHash = '') => {
  return `${tenantCd}_${sheetName}_${workBookName}_${filterHash}`;
};

/**
 * Fetch metrics data with Redux caching
 * Only fetches if not already cached for the current tenant
 */
export const fetchMetrics = (sheetName, workBookName) => {
  return async (dispatch, getState) => {
    const state = getState();
    const currentTenantCd = state.user.currentTenantCd;
    const applied = state.globalFilters?.applied;
    const filters = buildMetricsFilters(applied);
    const filterHash = getFilterHash(filters);
    const cacheKey = getCacheKey(currentTenantCd, sheetName, workBookName, filterHash);
    
    // Check if data is already cached
    const cachedData = state.metrics.cache[cacheKey];
    if (cachedData?.data) {
      // Return cached data immediately
      return Promise.resolve(cachedData.data);
    }

    const existingRequest = inFlightMetricsRequests.get(cacheKey);
    if (existingRequest) {
      return existingRequest;
    }

    // Dispatch request action
    dispatch({
      type: Types.FETCH_METRICS_REQUEST,
      payload: { cacheKey }
    });

    const requestPromise = (async () => {
      try {
        const data = await getMetricsData(sheetName, workBookName, filters);

        // Dispatch success and cache the data
        dispatch({
          type: Types.FETCH_METRICS_SUCCESS,
          payload: {
            cacheKey,
            data,
            timestamp: Date.now()
          }
        });

        return data;
      } catch (error) {
        dispatch({
          type: Types.FETCH_METRICS_FAILURE,
          payload: {
            cacheKey,
            error: error.message
          }
        });
        throw error;
      } finally {
        inFlightMetricsRequests.delete(cacheKey);
      }
    })();

    inFlightMetricsRequests.set(cacheKey, requestPromise);
    return requestPromise;
  };
};

/**
 * Clear all metrics cache
 * Call this when tenant changes or on manual refresh
 */
export const clearMetricsCache = () => ({
  type: Types.CLEAR_METRICS_CACHE
});

/**
 * Clear specific metrics cache by key
 */
export const clearMetricsCacheByKey = (tenantCd, sheetName, workBookName) => {
  const cacheKey = getCacheKey(tenantCd, sheetName, workBookName);
  return {
    type: Types.CLEAR_METRICS_CACHE,
    payload: { cacheKey }
  };
};

// Unified all-metrics VDS cache key: tenant + normalized filter hash
export const getAllMetricsCacheKey = (tenantCd, filterHash = '') => {
  return `${tenantCd}_${filterHash}`;
};

const inFlightAllMetricsRequests = new Map();

/**
 * Build PerformanceMetricsRequestDto body from applied global filters.
 */
export function buildAllMetricsBody(tenantCd, applied) {
  const personIdValue = applied?.personIdValue || '';
  const personIds = personIdValue
    .split(/[;\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const includePriorComparison = applied?.enablePriorComparison !== false;

  return {
    tenantCd,
    dateBasis: applied?.dateBasis || 'paid',
    currentStart: toIsoDate(applied?.currentPeriodFrom),
    currentEnd: toIsoDate(applied?.currentPeriodTo),
    priorStart: includePriorComparison ? toIsoDate(applied?.priorPeriodFrom) || undefined : undefined,
    priorEnd: includePriorComparison ? toIsoDate(applied?.priorPeriodTo) || undefined : undefined,
    division: applied?.divisionLabels?.length > 0 ? applied.divisionLabels : undefined,
    plan: applied?.planLabels?.length > 0 ? applied.planLabels : undefined,
    personIds: personIds.length > 0 ? personIds : undefined,
  };
}

/**
 * Fetch all metrics in a single VDS call and store in metrics cache under:
 *   ${tenantCd}_${filterHash}
 */
export const fetchAllMetrics = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const tenantCd = state.user.currentTenantCd;
    const applied = state.globalFilters?.applied;
    const defaultsSeeded = state.globalFilters?.defaultsSeeded;
    if (!tenantCd || !defaultsSeeded) {
      return;
    }

    const body = buildAllMetricsBody(tenantCd, applied);
    const filterHash = getFilterHash(buildMetricsFilters(applied));
    const cacheKey = getAllMetricsCacheKey(tenantCd, filterHash);

    const cachedData = state.metrics.cache[cacheKey];
    const isFreshCache = Boolean(
      cachedData?.timestamp &&
      (Date.now() - cachedData.timestamp) < METRICS_CACHE_TTL_MS
    );
    const hasValidCache = cachedData?.data &&
      typeof cachedData.data === 'object' &&
      !Array.isArray(cachedData.data) &&
      Object.keys(cachedData.data).length > 0 &&
      isFreshCache;
    if (hasValidCache) {
      return Promise.resolve(cachedData.data);
    }

    const existingRequest = inFlightAllMetricsRequests.get(cacheKey);
    if (existingRequest) {
      return existingRequest;
    }

    dispatch({
      type: Types.FETCH_ALL_METRICS_REQUEST,
      payload: { cacheKey }
    });

    const requestPromise = (async () => {
      try {
        const data = await getAllPerformanceMetrics(body);

        dispatch({
          type: Types.FETCH_ALL_METRICS_SUCCESS,
          payload: {
            cacheKey,
            data,
            timestamp: Date.now()
          }
        });

        return data;
      } catch (error) {
        dispatch({
          type: Types.FETCH_ALL_METRICS_FAILURE,
          payload: {
            cacheKey,
            error: error.message
          }
        });
        throw error;
      } finally {
        inFlightAllMetricsRequests.delete(cacheKey);
      }
    })();

    inFlightAllMetricsRequests.set(cacheKey, requestPromise);
    return requestPromise;
  };
};
